/* iceberg-ui */
import { DonutChart } from '@auviknetworks/iceberg-ui';

/* MUI */
import { Box, TableCell, TableRow, Typography } from '@mui/material';

/* Shared */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { LifecyclePicker } from 'application/Shared/Components/Pickers';
import { popoverContentsColumns } from 'application/Shared/Components/Recharts/PopoverContents';
import { ManagedTable } from 'application/Shared/Components/Table';

/* Objects */
import { Application } from 'objects/Application';

/* React */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// APPLICATION LIFECYCLE STAGE BREAKDOWN - 1st tab
export const StatusReports = ({
    renderingScheduledReport,
    reportData,
    reportWrapper
}) => {
    if (typeof reportData === 'undefined') {
        return null;
    }

    return (
        <Box width="100%">
            {reportData.statusApplicationLifecycleStageOverview && (
                <StatusOverview
                    renderingScheduledReport={renderingScheduledReport}
                    reportData={
                        reportData.statusApplicationLifecycleStageOverview
                    }
                    reportWrapper={reportWrapper}
                />
            )}

            {reportData.statusApplicationLifecycleStageOverviewBreakdown && (
                <StatusOverviewBreakdown
                    renderingScheduledReport={renderingScheduledReport}
                    reportData={
                        reportData.statusApplicationLifecycleStageOverviewBreakdown
                    }
                    reportWrapper={reportWrapper}
                />
            )}
            {reportData.statusApplicationLifecycleStageBreakdown && (
                <StatusBreakdown
                    renderingScheduledReport={renderingScheduledReport}
                    reportData={
                        reportData.statusApplicationLifecycleStageBreakdown
                    }
                />
            )}
        </Box>
    );
};

const StatusOverview = ({
    renderingScheduledReport,
    reportData,
    reportWrapper
}) => {
    const { t } = useTranslation();

    const statuses = [];
    Object.entries(reportData.data).forEach(([name, value]) => {
        if (value !== 0) {
            statuses.push({
                name: t(`Common.${name}`),
                value
            });
        }
    });

    return (
        <Box mt="32px" width="100%">
            {!reportWrapper && (
                <Typography variant="h5">
                    {t('Reports.ProceduralCodes.Overview')}
                </Typography>
            )}
            <DonutChart
                data={statuses}
                emptyMessage={t('Common.noData')}
                isAnimationActive={!renderingScheduledReport}
                popoverContentsColumns={popoverContentsColumns}
                responsiveContainerHeight={250}
            />
        </Box>
    );
};

const headersStatusOverviewBreakdown = (t) => [
    {
        align: 'left',
        field: 'ilo_status',
        renderHeader: <strong>{t('Common.lifecycleStage')}</strong>,
        width: '50%'
    },
    {
        align: 'left',
        field: 'ilo_total_applications',
        renderHeader: <strong>{t('Common.total_apps')}</strong>,
        width: '25%'
    },
    {
        align: 'right',
        field: 'ilo_percent_applications',
        renderHeader: <strong>{t('Common.percent')}</strong>,
        width: '25%'
    }
];

const StatusOverviewBreakdown = ({
    renderingScheduledReport,
    reportData,
    reportWrapper
}) => {
    const { t } = useTranslation();

    const categories = Object.entries(reportData.data).reduce(
        (acc, [name, value]) => {
            if (value.count) {
                acc.push({ count: value.count, name, percent: value.percent });
            }
            return acc;
        },
        []
    );

    const rows = useMemo(() => categories, [categories]);

    const rowsPerPage = renderingScheduledReport ? -1 : 25;

    const tableHeadersStatusOverviewBreakdown = useMemo(
        () => headersStatusOverviewBreakdown(t),
        [t]
    );

    return (
        <Box mt="32px" width="100%">
            {!reportWrapper && (
                <Typography variant="h5">
                    {t('Reports.ProceduralCodes.OverviewBreakdown')}
                </Typography>
            )}
            <ManagedTable
                headers={tableHeadersStatusOverviewBreakdown}
                manageRows
                noEmptyRows
                renderBody={(row) => (
                    <React.Fragment key={`ilo_${row.name}`}>
                        <TableRow
                            sx={{ backgroundColor: 'table.rowBackground' }}
                        >
                            <TableCell>
                                <LifecyclePicker visualOnly value={row.name} />
                            </TableCell>
                            <TableCell align="left">{row.count}</TableCell>
                            <TableCell align="right">
                                {`${row.percent}%`}
                            </TableCell>
                        </TableRow>
                    </React.Fragment>
                )}
                renderBodyKey="name"
                renderFooter={false}
                rows={rows}
                rowsPerPage={rowsPerPage}
                searchEnabled={false}
                sortDirection="ASC"
                sortFieldType="string"
                sortKey="name"
            />
        </Box>
    );
};

const headersStatusBreakdown = (t) => [
    {
        align: 'left',
        field: 'ilo_application',
        renderHeader: <strong>{t('Common.application')}</strong>,
        width: '40%'
    },
    {
        align: 'left',
        field: 'ilo_breakdown_status',
        renderHeader: <strong>{t('Common.lifecycleStage')}</strong>,
        width: '40%'
    },
    {
        align: 'right',
        field: 'ilo_breakdown_total',
        renderHeader: <strong>{t('Common.users')}</strong>,
        width: '20%'
    }
];

// For APPLICATION LIFECYCLE STAGE BREAKDOWN Report
const StatusBreakdown = ({ renderingScheduledReport, reportData }) => {
    const { t } = useTranslation();

    const rowsPerPage = renderingScheduledReport ? -1 : 25;

    const rows = useMemo(() => reportData.data, [reportData.data]);

    const tableHeadersStatusBreakdown = useMemo(
        () => headersStatusBreakdown(t),
        [t]
    );

    return (
        <Box className="break-before" mt="32px" width="100%">
            <Typography variant="h5">
                {t('Reports.ProceduralCodes.Breakdown')}
            </Typography>
            <ManagedTable
                headers={tableHeadersStatusBreakdown}
                manageRows
                renderBody={(row) => (
                    <React.Fragment key={`ilo_${row.applicationId}`}>
                        <TableRow
                            sx={{ backgroundColor: 'table.rowBackground' }}
                        >
                            <TableCell>
                                <Application data={row.application} nameHasLink>
                                    <ApplicationNameLinkCell nameNoTruncate />
                                </Application>
                            </TableCell>
                            <TableCell align="left">
                                <LifecyclePicker
                                    visualOnly
                                    value={row.status}
                                />
                            </TableCell>
                            <TableCell align="right">
                                {row.userCount}
                                <br />
                                &nbsp;
                            </TableCell>
                        </TableRow>
                    </React.Fragment>
                )}
                renderBodyKey="applicationId"
                renderFooter={!renderingScheduledReport}
                rows={rows}
                rowsPerPage={rowsPerPage}
                searchEnabled={false}
                sortDirection="DESC"
                sortFieldType="string"
                sortKey="status"
            />
        </Box>
    );
};
